import React, { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { useReactToPrint } from "react-to-print";

import CloseIcon from "@mui/icons-material/Close";
import LocalPrintshopOutlinedIcon from "@mui/icons-material/LocalPrintshopOutlined";
import LocationCityIcon from "@mui/icons-material/LocationCity";
import TodayOutlinedIcon from "@mui/icons-material/TodayOutlined";
import { Box, Container, IconButton, Input, Stack, Typography } from "@mui/material";
import { VisibilityLock } from "assets/material-custom-icons";
import { AsyncContentMui } from "components";
import { DealRoleGuard } from "components/guards";
import { LeftSlider } from "components/modals";
import { FilesPickerModal } from "components/modals/Files-picker/FilesPicker";
import { FilesPickerInfoModal } from "components/modals/Files-picker/FilesPickerInfoModal";
import { MaterialTooltip } from "components/tooltips/MaterialTooltip";
import { dealFormFilters } from "constants/deal-form";
import { Routes } from "constants/routes";
import {
  useActionHook,
  useCommentsSocketHook,
  useCommentsUrlHooks,
  useDidUpdate,
  useFormatDateHook,
  useNavigationHooks,
  useRoleGuardHook,
  useSurveySocketHook
} from "hooks";
import { debounce } from "lodash";
import {
  useCommentsActions,
  useDealActions,
  useDealCSSelector,
  useDealFormAiDataSelect,
  useDealFormDataSelect,
  useDealFormLoadingSelect,
  useDealIdSelector,
  useViewActions
} from "store";
import { usePickUserPermissions } from "store/selectors/user-selectors";
import { DealFormFilter, ICapitalSeeker, IFile, UserPermissionType } from "store/types";

import { RoleName } from "../../constants";
import { AiFilesList } from "./AiFileslist/AiFilesList";
import { DescriptionEditor } from "./Description-editor/DescriptionEditor";
import { Header } from "./Header/Header";
import { UpdateBar } from "./Update-bar/UpdateBar";

const LazyBody = lazy(() => import("./Body/Body").then((module) => ({ default: module.Body })));

const MemoizedBody = React.memo(
  ({ setFilterValue }: { setFilterValue: React.Dispatch<React.SetStateAction<DealFormFilter>> }) => (
    <Suspense
      fallback={
        <AsyncContentMui isLoading={true}>
          <></>
        </AsyncContentMui>
      }
    >
      <LazyBody setFilterValue={setFilterValue} />
    </Suspense>
  )
);

MemoizedBody.displayName = "MemoizedBody";

/**
 * Global Form component — can be used outside of deal context (e.g. in Global Workspace).
 * Gets dealId and formId from URL params (workspaceDealFolderForm route) and is not bound to deal-scoped routes.
 */
interface FormProps {
  onClose?: () => void;
}

export const Form: React.FC<FormProps> = ({ onClose }) => {
  const history = useHistory();
  const { deal_id: dealIdParam, form_id: formIdParam } = useParams<{ deal_id?: string; form_id?: string }>();
  const dealId = dealIdParam != null ? Number(dealIdParam) : NaN;
  const formId = formIdParam != null ? Number(formIdParam) : NaN;
  const isValidParams = !Number.isNaN(formId);

  const { getDealFormAndStoreAction, clearDealFormAction, updateFormAction } = useDealActions();
  const { commentsScrollAction, setShareDialog } = useViewActions();
  const { getCommentAction } = useCommentsActions();

  const {
    id: dealFormId,
    is_private: isFormPrivate,
    created_at: createdAt,
    title: formTitle,
    deal_title: dealTitle
  } = useDealFormDataSelect() || {};

  const loading = useDealFormLoadingSelect();
  const isOutsideDeal = !useDealIdSelector();
  const formAiData = useDealFormAiDataSelect();
  const { commentId } = useCommentsUrlHooks();
  const { removeParams } = useNavigationHooks();
  const { runSubscriptions: surveySubscribe } = useSurveySocketHook();
  const { runSubscriptions } = useCommentsSocketHook();
  const formatDate = useFormatDateHook();
  const capitalSeekers = useDealCSSelector();
  const isInsideDeal = Boolean(useDealIdSelector());

  const capitalSeeker: ICapitalSeeker | undefined = capitalSeekers?.[0];

  const containerRef = useRef<HTMLDivElement>(null);

  const reactToPrintFn = useReactToPrint({ contentRef: containerRef });

  // Editable title state
  const [formTitleValue, setFormTitleValue] = useState<string>(formTitle || "");
  const prevFormTitleRef = useRef<string>(formTitle || "");

  const myDeals = usePickUserPermissions("deals");
  const noAccessPermission = myDeals && myDeals === UserPermissionType.NO_ACCESS;
  const isViewerPermission = myDeals && myDeals === UserPermissionType.VIEWER;

  const titleInputEnabled = useRoleGuardHook(
    true,
    [RoleName.Admin, RoleName.Analyst],
    noAccessPermission || isViewerPermission
  );

  useDidUpdate(() => {
    if (formTitle && formTitle !== formTitleValue) {
      setFormTitleValue(formTitle);
      prevFormTitleRef.current = formTitle;
    }
  }, [formTitle]);

  const debouncedUpdateTitle = useMemo(
    () =>
      debounce((title: string) => {
        if (!isValidParams) return;
        prevFormTitleRef.current = title;
        updateFormAction({ dealId, formId, title });
      }, 1000),
    [dealId, formId, isValidParams, updateFormAction]
  );

  const handleFormTitleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setFormTitleValue(e.target.value);
      debouncedUpdateTitle(e.target.value);
    },
    [debouncedUpdateTitle]
  );

  const handleFormTitleBlur = useCallback(() => {
    debouncedUpdateTitle.flush();
  }, [debouncedUpdateTitle]);

  const handleFormTitleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter") {
        (e.target as HTMLInputElement).blur();
      } else if (e.key === "Escape") {
        debouncedUpdateTitle.cancel();
        setFormTitleValue(prevFormTitleRef.current);
        setTimeout(() => (e.target as HTMLInputElement).blur());
      }
    },
    [debouncedUpdateTitle]
  );

  const [statusFilterValue, setStatusFilterValue] = useState<DealFormFilter>(dealFormFilters[0]);
  const [filesPickerModalOpen, setFilesPickerModalOpen] = useState<boolean>(false);
  const [filesPickerInfoModal, setFilesPickerInfoModal] = useState<{
    infoModalOpen: boolean;
    isSendAiAvailable: boolean;
  } | null>(null);

  const shouldShowAiFilesList = useMemo(() => formAiData.ai_status, [formAiData.ai_status]);
  const shouldShowDescriptionEditor = useMemo(() => dealFormId, [dealFormId]);

  const getForm = useCallback(
    async (dId: number, fId: number) => {
      try {
        await getDealFormAndStoreAction({ dealId: dId, formId: fId });
        surveySubscribe();
        await Promise.all([
          getCommentAction({ dealId: dId, formId: fId, general: true }),
          getCommentAction({ dealId: dId, formId: fId })
        ]);
        runSubscriptions();
        if (commentId) {
          commentsScrollAction(true);
        }
      } catch {
        history.replace(Routes.workspaceDeals);
      }
    },
    [
      getDealFormAndStoreAction,
      getCommentAction,
      surveySubscribe,
      runSubscriptions,
      commentId,
      commentsScrollAction,
      history
    ]
  );

  const { getFilesAction, aiAnswerFormAction, getFoldersAction } = useDealActions();
  const { action: aiAnswerForm } = useActionHook(aiAnswerFormAction);

  const filesPickerSubmit = useCallback(
    async ({
      filesIds = [],
      attachments,
      dealId: selectedDealId
    }: {
      filesIds?: number[];
      files?: IFile[];
      attachments: File[];
      dealId?: number | null;
    }) => {
      if (!isValidParams) return;
      // Global forms (e.g. /workspace/folder/:id/form/:id) have no deal in the URL — fall back to the deal
      // the user picked inside the files picker.
      const effectiveDealId = !Number.isNaN(dealId) ? dealId : selectedDealId ?? undefined;
      await aiAnswerForm({ dealId: effectiveDealId, formId, file_ids: filesIds, attachments });
      if (effectiveDealId) {
        await getFoldersAction({ dealId: effectiveDealId });
        await getFilesAction(effectiveDealId);
      }
      await getDealFormAndStoreAction({ dealId: effectiveDealId, formId, disableLoading: true });
      setFilesPickerInfoModal({ infoModalOpen: true, isSendAiAvailable: true });
    },
    [dealId, formId, isValidParams, aiAnswerForm, getFoldersAction, getFilesAction, getDealFormAndStoreAction]
  );

  const openShareDialog = useCallback(() => {
    if (!formTitle || !dealFormId || !isValidParams) return;
    setShareDialog({
      type: "form_id",
      elements: [dealFormId],
      shareName: formTitle,
      dealId: dealId,
      dealCapitalSeekerEmail: capitalSeeker?.email,
      itemToShare: isFormPrivate ? null : dealFormId
    });
  }, [formTitle, dealFormId, dealId, capitalSeeker, isFormPrivate, isValidParams, setShareDialog]);

  const updateFormRecurring = useCallback(
    async (date: string) => {
      if (!isValidParams) return;
      await updateFormAction({
        dealId,
        formId,
        recurring_date: date
      });
    },
    [dealId, formId, isValidParams, updateFormAction]
  );

  useEffect(() => {
    if (formId && !Number.isNaN(formId)) {
      setStatusFilterValue(dealFormFilters[0]);
    }
    removeParams(["view"]);
  }, [formId]);

  useEffect(() => {
    return () => {
      clearDealFormAction();
    };
  }, [clearDealFormAction]);

  // Fetch form only when route params change; omit getForm/history from deps to avoid
  // infinite loop (getForm is recreated after store updates from the fetch).
  useEffect(() => {
    if (formId && !Number.isNaN(formId)) {
      getForm(dealId, formId);
    } else {
      history.replace(Routes.workspaceDeals);
    }
  }, [dealId, formId]);

  if (!isValidParams) {
    return null;
  }

  return (
    <AsyncContentMui
      isLoading={loading}
      overlay
      boxProps={{
        maxHeight: "100%",
        overflow: "hidden",
        flexGrow: 1,
        justifyContent: "space-between",
        flexDirection: "column"
      }}
      spinnerProps={{ size: 80 }}
    >
      <UpdateBar filterValue={statusFilterValue} />
      <Stack
        sx={{
          p: "8px 16px 16px 16px",
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "center",
          width: "100%"
        }}
      >
        <Input
          value={formTitleValue}
          disabled={!titleInputEnabled}
          disableUnderline
          onChange={handleFormTitleChange}
          onBlur={handleFormTitleBlur}
          onKeyDown={handleFormTitleKeyDown}
          sx={{
            fontSize: "2.125rem",
            fontWeight: 500,
            color: "#0A3142",
            flexGrow: 1,
            borderRadius: "4px",
            padding: "2px 6px",
            transition: "background-color 0.2s, box-shadow 0.2s",
            "&:hover:not(.Mui-disabled)": {
              backgroundColor: "#F6F8FC"
            },
            "&.Mui-focused": {
              backgroundColor: "#F6F8FC",
              boxShadow: "0 1px 0 0 #26A6AE"
            },
            "& input": { padding: 0 },
            "& input.Mui-disabled": {
              color: "#0A3142",
              WebkitTextFillColor: "#0A3142"
            }
          }}
          inputProps={{ dir: "auto" }}
        />
        {onClose && (
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        )}
      </Stack>
      <Stack
        sx={{
          p: "8px 16px",
          flexDirection: "row",
          width: "100%",
          gap: "10px",
          borderTop: "1px solid #DBE8F5",
          borderBottom: "1px solid #DBE8F5",
          marginBottom: "16px",
          justifyContent: "space-between"
        }}
      >
        <Stack sx={{ flexDirection: "row", gap: "4px", alignItems: "center" }}>
          {dealTitle && (
            <Stack sx={{ flexDirection: "row", gap: "4px", alignItems: "center" }}>
              <LocationCityIcon />
              <Typography noWrap variant="body1" sx={{ color: "#496682", fontWeight: "500" }}>
                Deal:
              </Typography>
              <MaterialTooltip isShow title={dealTitle} disableHoverListener={dealTitle.length <= 20}>
                <Typography noWrap variant="body1" sx={{ color: "#26A6AE", fontWeight: "400", maxWidth: "350px" }}>
                  {dealTitle}
                </Typography>
              </MaterialTooltip>
            </Stack>
          )}
          <Stack sx={{ flexDirection: "row", gap: "4px", alignItems: "center" }}>
            <TodayOutlinedIcon />
            <Typography variant="body1" sx={{ color: "#496682", fontWeight: "500" }}>
              Add Date:
            </Typography>
            <Typography variant="body1" sx={{ color: "#496682", fontWeight: "400" }}>
              {formatDate({ date: createdAt })}
            </Typography>
          </Stack>
        </Stack>
        <Stack sx={{ flexDirection: "row", gap: "10px", alignItems: "center" }}>
          <MaterialTooltip isShow title="Print">
            <IconButton onClick={reactToPrintFn}>
              <LocalPrintshopOutlinedIcon sx={{ fill: "#071F35" }} />
            </IconButton>
          </MaterialTooltip>
        </Stack>
      </Stack>
      <Container
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "flex-start",
          alignItems: "stretch",
          borderRadius: "8px",
          backgroundColor: "#f7f9fc",
          flexGrow: 1,
          maxHeight: "100%",
          overflow: "auto",
          border: "6px solid #f7f9fc",
          borderLeft: "none",
          padding: "0px",
          position: "relative",
          mb: "14px",
          "@media (max-width: 1366px)": { maxWidth: "850px", minWidth: "850px" },
          "@media (max-width: 850px)": { maxWidth: "100%", minWidth: "100%" },
          "::-webkit-scrollbar": {
            width: "13px"
          },
          "::-webkit-scrollbar-thumb": {
            backgroundColor: "#bac7ce",
            borderRadius: "8px",
            backgroundClip: "content-box",
            border: "2px solid transparent"
          },
          "::-webkit-scrollbar-track": {
            backgroundColor: "#f2f2f2",
            borderRadius: "8px"
          }
        }}
        maxWidth="xl"
      >
        {isFormPrivate && <VisibilityLock sx={{ position: "absolute", right: "10px", top: "8px", fill: "#BAC7CE" }} />}
        <Container
          ref={containerRef}
          sx={{ maxWidth: "1000px", width: "91%", flexGrow: 1, paddingBottom: "2px" }}
          maxWidth={false}
        >
          <Header
            openShareDialog={openShareDialog}
            filterValue={statusFilterValue}
            setFilterValue={setStatusFilterValue}
            updateFormRecurring={updateFormRecurring}
            setFilesPickerModalOpen={setFilesPickerModalOpen}
          />
          <Box
            sx={{
              backgroundColor: "white",
              borderRadius: "0 0 8px 8px",
              padding: "20px",
              boxShadow: "0 1px 2px 0 rgba(18, 18, 23, 0.10)"
            }}
          >
            {shouldShowAiFilesList && (
              <DealRoleGuard availableRoles={[RoleName.Admin, RoleName.Analyst]}>
                <AiFilesList setFilesPickerModalOpen={setFilesPickerModalOpen} />
              </DealRoleGuard>
            )}
            {shouldShowDescriptionEditor && <DescriptionEditor />}
            <MemoizedBody setFilterValue={setStatusFilterValue} />
          </Box>
        </Container>
      </Container>
      <FilesPickerModal
        filesPickerModalOpen={filesPickerModalOpen}
        onClose={() => setFilesPickerModalOpen(false)}
        getFilesOutsideDeal={isOutsideDeal}
        currentFiles={formAiData?.ai_files || []}
        filesPickerSubmit={filesPickerSubmit}
        title="Ask Ai"
        workspaceTitle="Select or upload files"
        dealId={dealId}
      />
      <FilesPickerInfoModal
        filesPickerInfoModal={filesPickerInfoModal}
        setFilesPickerInfoModal={setFilesPickerInfoModal}
      />
      {!isInsideDeal && <LeftSlider />}
    </AsyncContentMui>
  );
};
